import type { AccountBase } from '@/models/AccountViewModel';
import {
  ROOT_CATEGORY_TAG_KEY,
  accountGroupTagStorageKey,
} from '@/utils/accountGroupTagAssignments';

export type GroupByValue =
  | 'none'
  | 'IsHome'
  | 'InstitutionId'
  | 'ClassificationId';

export type CustomGroupBy = `investor:${number}` | `advisor:${number}`;

export type AccountsGroupBy = GroupByValue | CustomGroupBy;

export function isCustomGroupBy(raw: string): raw is CustomGroupBy {
  return ROOT_CATEGORY_TAG_KEY.test(raw);
}

export function customGroupBy(
  categoryId: number,
  isAdvisorTag: boolean,
): CustomGroupBy {
  return accountGroupTagStorageKey({
    categoryId,
    isAdvisorTag,
  }) as CustomGroupBy;
}

/**
 * Firm-configurable option label. `termKey` is the friendly term name (see
 * eslint-local-rules/no-raw-configurable-term.js); it must be resolved with a
 * bare `useLocalization().t(termKey)` so a firm's locale override applies.
 * Passing the raw i18n key instead would bypass the override lookup.
 */
interface ConfigurableGroupByOption {
  value: GroupByValue;
  termKey: string;
  labelKey?: never;
  defaultLabel?: never;
}

/** Option whose label is not firm-configurable; plain i18n with fallback copy. */
interface PlainGroupByOption {
  value: GroupByValue;
  termKey?: never;
  labelKey: string;
  defaultLabel: string;
}

export type GroupByOption = ConfigurableGroupByOption | PlainGroupByOption;

export const GROUP_BY_OPTIONS: readonly GroupByOption[] = [
  { value: 'none', labelKey: 'noGrouping', defaultLabel: 'No Grouping' },
  {
    value: 'IsHome',
    termKey: 'AUM',
  },
  {
    value: 'InstitutionId',
    labelKey: 'institution',
    defaultLabel: 'Institution',
  },
  {
    value: 'ClassificationId',
    labelKey: 'balanceSheetCategory',
    defaultLabel: 'Balance Sheet Category',
  },
] as const;

export interface ServerGroupSpec {
  ValueProperty: string;
  DisplayProperty: string;
  SortProperty?: string;
}

export function serverGroupSpecFor(
  groupBy: string,
): ServerGroupSpec | undefined {
  switch (groupBy) {
    case 'IsHome':
      return { ValueProperty: 'IsHome', DisplayProperty: 'IsHomeDisplay' };
    case 'InstitutionId':
      return {
        ValueProperty: 'InstitutionId',
        DisplayProperty: 'InstitutionName',
      };
    case 'ClassificationId':
      return {
        ValueProperty: 'ClassificationId',
        DisplayProperty: 'ClassificationName',
        SortProperty: 'ClassificationSort',
      };
    default:
      return isCustomGroupBy(groupBy)
        ? { ValueProperty: groupBy, DisplayProperty: groupBy }
        : undefined;
  }
}

const SERVER_GROUP_KEY_SEPARATOR = '\u001f';

export function serverGroupKey(
  value: string | number | null | undefined,
  label: string,
): string {
  if (value === null || value === undefined || value === '') return label;
  return `${String(value)}${SERVER_GROUP_KEY_SEPARATOR}${label}`;
}

export function serverGroupLabel(key: string): string {
  const separatorIndex = key.indexOf(SERVER_GROUP_KEY_SEPARATOR);
  return separatorIndex === -1 ? key : key.slice(separatorIndex + 1);
}

/**
 * Maps a group-by value to the DataGrid rowGroupingModel (grid field names).
 * Returns [] for 'none'/unknown. `investor:*`/`advisor:*` custom tag groups map
 * to the derived 'CustomGroup' field (preserves existing portfolio behavior).
 */
export function groupingModelFor(groupBy: string): string[] {
  switch (groupBy) {
    case 'institution':
      return ['InstitutionName'];
    case 'data source':
      return ['DataSource'];
    case 'accountType':
      return ['formattedSubtype'];
    case 'relationship':
      return ['HeldBy'];
    default:
      if (groupBy.startsWith('investor:') || groupBy.startsWith('advisor:')) {
        return ['CustomGroup'];
      }
      return [];
  }
}

/**
 * Derives the scalar "held by" label for an account from its Investors array,
 * used as the grouping key for 'relationship' grouping. Falls back to the
 * supplied `unassignedLabel` (localized by the caller; defaults to 'Unassigned').
 */
export function deriveHeldBy(
  investors: AccountBase['Investors'],
  unassignedLabel = 'Unassigned',
): string {
  if (!Array.isArray(investors) || investors.length === 0) {
    return unassignedLabel;
  }
  const names = investors
    // DB rows are inconsistent — an Investors array can contain null entries,
    // so guard each element even though the type says it is always present.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    .map((investor) => investor?.InvestorName ?? investor?.Name ?? '')
    .filter((name) => name.length > 0);
  return names.length ? names.join(', ') : unassignedLabel;
}

/** The API sends this literal where an account has no classification tier. */
const NOT_SET = 'not set';

/**
 * Builds the Account Type label the legacy grid shows: the classification, then
 * the sub-classification after a slash when the account is classified that
 * deeply — "Investments / Rollover IRA". An account with no classification at
 * all reads as the supplied `notSetLabel`.
 *
 * Values are passed through as the feed sends them rather than title-cased, so
 * that acronyms survive: legacy shows "Rollover IRA", not "Rollover Ira".
 */
export function formatAccountTypeLabel(
  classificationName: string | null | undefined,
  subClassificationName: string | null | undefined,
  notSetLabel = 'Not set',
): string {
  const classification = classificationName?.trim();
  if (!classification || classification.toLowerCase() === NOT_SET) {
    return notSetLabel;
  }
  const subClassification = subClassificationName?.trim();
  if (!subClassification || subClassification.toLowerCase() === NOT_SET) {
    return classification;
  }
  return `${classification} / ${subClassification}`;
}

/**
 * Derives the title-cased "account type" label for an account, used as the
 * grouping key for 'accountType' grouping. Prefers the sub-classification,
 * falls back to the classification, and — when both are missing (DB rows can
 * omit both) — returns the supplied `notSetLabel` (localized by the caller;
 * defaults to 'Not set') so those rows land in a labelled bucket rather than a
 * blank group header.
 */
export function formatSubtype(
  subClassificationName: string | null | undefined,
  classificationName: string | null | undefined,
  notSetLabel = 'Not set',
): string {
  const raw = subClassificationName || classificationName || '';
  if (!raw) return notSetLabel;
  return raw
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
